import { sessionFetch as fetch } from "../../../auth/session-request.js";
import type {
  AdminAuthSummary,
  AdminJob,
  AdminJobId,
  AdminJobRun,
  AdminTab,
  CloseConfigRows,
  CloseConfigPreviewJobId,
  CloseConfigPreviewResult,
  CloseConfigSeedId,
  CloseConfigSeedSummary,
  CloseConfigValidationResult,
  MappingDryRunResult,
  MappingProposal,
  MappingRule,
  MappingRuleRow,
  MappingValidationResult,
} from "../types/mapping.js";
import type {
  AdminBatchDrillback,
  AdminBronzeDrillback,
  AdminDrillbackSearchResult,
  AdminLineDrillback,
  AdminSilverDrillback,
} from "../types/drillback.js";
import { loadShellEnv } from "../../../env.js";
import {
  MappingReferenceResponseSchema,
  type MappingReferenceResponse,
} from "../types/mapping-reference.js";
import type { AdminDashboardFast, AdminDashboardSources } from "../types/dashboard.js";

const API_BASE = loadShellEnv().adminApiBase;

export async function getMappingReference(): Promise<MappingReferenceResponse> {
  const response = await fetch(`${API_BASE}/api/admin/mapping-reference`, {
    headers: authHeaders(),
  });
  return MappingReferenceResponseSchema.parse(await parseJsonResponse(response));
}

/**
 * Returns the current in-memory Okta ACCESS token, or undefined when signed out.
 *
 * The admin API (`/api/admin/*`) sits behind the shared Lambda authorizer whose identity source is
 * the `Authorization` header (infrastructure/lib/constructs/aire-app-api.ts). Every request must
 * therefore present `Authorization: Bearer <access token>` — the authorizer validates `aud` against
 * the API resource audience `api://lio-aire`, which appears on access tokens only. An ID token here
 * is denied on the `aud` check and API Gateway answers a bare 403 `Forbidden` before the handler
 * Lambda runs (#120). The shell's `RequireAuth` registers this provider once auth resolves; it is
 * read per request so token refreshes are picked up without re-instantiating the client. The
 * default returns undefined so unit tests that don't exercise auth still work against a stubbed
 * fetch.
 */
type AdminApiTokenProvider = () => string | undefined;

let getAdminApiToken: AdminApiTokenProvider = () => undefined;

export function setAdminApiTokenProvider(provider: AdminApiTokenProvider): void {
  getAdminApiToken = provider;
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = getAdminApiToken();
  if (token !== undefined && token !== "") {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function getAdminTabs(): Promise<{
  readonly tabs: AdminTab[];
  readonly auth: AdminAuthSummary;
}> {
  const response = await fetch(`${API_BASE}/api/admin/tabs`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly tabs: AdminTab[];
    readonly auth: AdminAuthSummary;
  };
}

export async function getAdminDashboard(): Promise<AdminDashboardFast> {
  return adminGet("/api/admin/dashboard");
}

// Snowflake-backed tiles, fetched separately so a slow or resuming warehouse never blocks the fast
// tiles above (see docs on AdminDashboardSources).
export async function getAdminDashboardSources(): Promise<AdminDashboardSources> {
  return adminGet("/api/admin/dashboard/sources");
}

export async function createAdminWebSocketTicket(): Promise<{
  readonly ticket: string;
  readonly expiresAt: string;
  readonly webSocketUrl: string;
}> {
  const response = await fetch(`${API_BASE}/api/admin/websocket-ticket`, {
    method: "POST",
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly ticket: string;
    readonly expiresAt: string;
    readonly webSocketUrl: string;
  };
}

export async function getAdminBatchDrillback(batchId: string): Promise<AdminBatchDrillback> {
  return adminGet(`/api/admin/drillback/batch/${encodeURIComponent(batchId)}`);
}

export async function getAdminLineDrillback(lineId: string): Promise<AdminLineDrillback> {
  return adminGet(`/api/admin/drillback/line/${encodeURIComponent(lineId)}`);
}

export async function getAdminSilverDrillback(eventId: string): Promise<AdminSilverDrillback> {
  return adminGet(`/api/admin/drillback/silver/${encodeURIComponent(eventId)}`);
}

export async function getAdminBronzeDrillback(eventId: string): Promise<AdminBronzeDrillback> {
  return adminGet(`/api/admin/drillback/bronze/${encodeURIComponent(eventId)}`);
}

export async function searchAdminDrillback(input: {
  readonly naturalKey: string;
  readonly source?: string;
  readonly entity?: string;
  readonly account?: string;
  readonly dateFrom?: string;
  readonly dateTo?: string;
}): Promise<readonly AdminDrillbackSearchResult[]> {
  const params = new URLSearchParams({ natural_key: input.naturalKey });
  appendSearchParam(params, "source", input.source);
  appendSearchParam(params, "entity", input.entity);
  appendSearchParam(params, "account", input.account);
  appendSearchParam(params, "date_from", input.dateFrom);
  appendSearchParam(params, "date_to", input.dateTo);
  const payload = await adminGet<{ readonly results: readonly AdminDrillbackSearchResult[] }>(
    `/api/admin/drillback/search?${params.toString()}`,
  );
  return payload.results;
}

export async function listAdminJobs(): Promise<{
  readonly jobs: AdminJob[];
  readonly recentRuns: AdminJobRun[];
}> {
  const response = await fetch(`${API_BASE}/api/admin/jobs`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as {
    readonly jobs: AdminJob[];
    readonly recentRuns: AdminJobRun[];
  };
}

export async function runAdminJob(input: {
  readonly jobId: AdminJobId;
  readonly parameters: Record<string, string>;
}): Promise<{ readonly run: AdminJobRun }> {
  const response = await fetch(`${API_BASE}/api/admin/jobs/${input.jobId}/run`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify(input.parameters),
  });
  return (await parseJsonResponse(response)) as { readonly run: AdminJobRun };
}

export async function listCloseConfigSeeds(): Promise<CloseConfigSeedSummary[]> {
  const response = await fetch(`${API_BASE}/api/admin/close-config/seeds`, {
    headers: authHeaders(),
  });
  const payload = (await parseJsonResponse(response)) as { seeds?: CloseConfigSeedSummary[] };
  return payload.seeds ?? [];
}

export async function getCloseConfigRows(seedId: CloseConfigSeedId): Promise<CloseConfigRows> {
  const response = await fetch(`${API_BASE}/api/admin/close-config/seeds/${seedId}/rows`, {
    headers: authHeaders(),
  });
  return (await parseJsonResponse(response)) as CloseConfigRows;
}

export async function validateCloseConfigRow(input: {
  readonly seedId: CloseConfigSeedId;
  readonly rowIndex: number;
  readonly proposedRecord: Record<string, string>;
}): Promise<CloseConfigValidationResult> {
  const response = await fetch(
    `${API_BASE}/api/admin/close-config/seeds/${input.seedId}/validate`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        row_index: input.rowIndex,
        proposed_record: input.proposedRecord,
      }),
    },
  );
  return (await parseJsonResponse(response)) as CloseConfigValidationResult;
}

export async function proposeCloseConfigChange(input: {
  readonly seedId: CloseConfigSeedId;
  readonly rowIndex: number;
  readonly proposedRecord: Record<string, string>;
  readonly rationale: string;
}): Promise<unknown> {
  const response = await fetch(`${API_BASE}/api/admin/close-config/seeds/${input.seedId}/propose`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      row_index: input.rowIndex,
      proposed_record: input.proposedRecord,
      rationale: input.rationale,
    }),
  });
  return parseJsonResponse(response);
}

export async function previewCloseConfigChange(input: {
  readonly seedId: CloseConfigSeedId;
  readonly rowIndex: number;
  readonly proposedRecord: Record<string, string>;
  readonly jobId: CloseConfigPreviewJobId;
  readonly period: string;
  readonly basis?: "GAAP" | "STAT" | "MGMT";
  readonly entity?: string;
  readonly targetName?: string;
  readonly reason: string;
}): Promise<CloseConfigPreviewResult> {
  const response = await fetch(`${API_BASE}/api/admin/close-config/seeds/${input.seedId}/preview`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({
      row_index: input.rowIndex,
      proposed_record: input.proposedRecord,
      job_id: input.jobId,
      period: input.period,
      basis: input.basis,
      entity: input.entity,
      target_name: input.targetName,
      reason: input.reason,
    }),
  });
  return (await parseJsonResponse(response)) as CloseConfigPreviewResult;
}

export async function listMappingRules(source: string): Promise<MappingRuleRow[]> {
  const response = await fetch(`${API_BASE}/api/admin/mapping/${source}/rules`, {
    headers: authHeaders(),
  });
  const payload = (await parseJsonResponse(response)) as { rules?: MappingRuleRow[] };
  return payload.rules ?? [];
}

export async function listMappingProposals(): Promise<MappingProposal[]> {
  const response = await fetch(`${API_BASE}/api/admin/mapping/proposals`, {
    headers: authHeaders(),
  });
  const payload = (await parseJsonResponse(response)) as { proposals?: MappingProposal[] };
  return payload.proposals ?? [];
}

export async function validateMappingRule(rule: MappingRule): Promise<MappingValidationResult> {
  const response = await fetch(
    `${API_BASE}/api/admin/mapping/${rule.source_name}/rules/${rule.mapping_id}/validate`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ proposed_rule: rule }),
    },
  );
  return (await parseJsonResponse(response)) as MappingValidationResult;
}

export async function dryRunMappingRule(rule: MappingRule): Promise<MappingDryRunResult> {
  const response = await fetch(
    `${API_BASE}/api/admin/mapping/${rule.source_name}/rules/${rule.mapping_id}/dry-run`,
    {
      method: "POST",
      headers: authHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({ proposed_rule: rule }),
    },
  );
  return (await parseJsonResponse(response)) as MappingDryRunResult;
}

export async function proposeMappingChange(input: {
  readonly rule: MappingRule;
  readonly rationale: string;
}): Promise<unknown> {
  const response = await fetch(`${API_BASE}/api/admin/mapping/${input.rule.source_name}/propose`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ proposed_rules: [input.rule], rationale: input.rationale }),
  });
  return parseJsonResponse(response);
}

async function parseJsonResponse(response: Response): Promise<unknown> {
  const rawBody = await response.text();
  let payload: unknown;
  try {
    payload = rawBody.length === 0 ? null : (JSON.parse(rawBody) as unknown);
  } catch {
    const contentType = response.headers.get("content-type") ?? "unknown content type";
    throw new Error(
      `Admin API returned a non-JSON response (${response.status}, ${contentType}). ` +
        "The request may have been routed to the web application instead of the API.",
    );
  }
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload !== null && "message" in payload
        ? String(payload.message)
        : `Request failed with ${response.status}`;
    throw new Error(message);
  }
  return payload;
}

async function adminGet<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  return (await parseJsonResponse(response)) as T;
}

function appendSearchParam(params: URLSearchParams, key: string, value: string | undefined): void {
  if (value !== undefined && value.trim().length > 0) {
    params.set(key, value.trim());
  }
}
